

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Grid, Icon, Snackbar, Alert } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewReferralCodeModal from 'components/NewReferralCodeModal';
import { formatDate } from 'utils/DateTimeConversion';

const ReferralCodes = () => {
    const [codes, setCodes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('referral_codes');

    useEffect(() => {
        fetchCodes();
    }, []);

    const fetchCodes = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/referral-codes/');
            setCodes(response.data);
        } catch (error) {
            console.error('Error fetching referral codes:', error);
            setSnackbar({ open: true, message: 'Error loading referral codes', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveCode = async (payload) => {
        try {
            await axiosInstance.post('/referral-codes/', payload);
            setSnackbar({ open: true, message: 'Referral Code created successfully', severity: 'success' });
            fetchCodes();
        } catch (error) {
            console.error('Error saving referral code:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/referral-code/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(row.original);
                    }}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Code",
            accessor: "code",
            width: "15%"
        },
        {
            Header: "Referrer",
            accessor: "referrer_id",
            width: "20%",
            Cell: ({ row }) => row.original.referrer?.name || row.original.referrer_id || '-'
        },
        {
            Header: "Status",
            accessor: "status",
            width: "12%"
        },
        {
            Header: "Expiration Date",
            accessor: "expiration_date",
            width: "15%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "12%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: codes,
    }), [codes]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Referral Codes</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Referral Code
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : codes.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                        onRowClick={handleRowClick}
                                    />
                                ) : (
                                    <EmptyState moduleName="Referral Code" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewReferralCodeModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveCode}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default ReferralCodes;

